import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { MemberNickname, dbRowToMemberNickname } from '@/types/memberNickname';

export function useNicknames(teamId: string | null) {
    const [nicknames, setNicknames] = useState<MemberNickname[]>([]);

    const fetchNicknames = useCallback(async () => {
        if (!teamId) {
            setNicknames([]);
            return;
        }

        const { data, error } = await supabase
            .from('member_nicknames')
            .select('*')
            .eq('team_id', teamId);

        if (error) {
            console.error('Error fetching nicknames:', error);
            return;
        }

        setNicknames(data ? data.map(dbRowToMemberNickname) : []);
    }, [teamId]);

    useEffect(() => {
        fetchNicknames();

        if (!teamId) return;

        const channel = supabase
            .channel(`nicknames-realtime-${teamId}`)
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'member_nicknames', filter: `team_id=eq.${teamId}` },
                () => {
                    fetchNicknames();
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [fetchNicknames, teamId]);

    const setNickname = useCallback(async (viewerId: string, targetId: string, nickname: string) => {
        if (!teamId) return;

        if (!nickname.trim()) {
            // Revert to normal name by deleting
            const { error } = await supabase
                .from('member_nicknames')
                .delete()
                .match({ team_id: teamId, viewer_id: viewerId, target_id: targetId });

            if (error) {
                console.error('Error deleting nickname:', error);
                throw error;
            }
            return;
        }

        const { error } = await supabase
            .from('member_nicknames')
            .upsert({
                team_id: teamId,
                viewer_id: viewerId,
                target_id: targetId,
                nickname: nickname.trim(),
            }, { onConflict: 'viewer_id,target_id' });

        if (error) {
            console.error('Error setting nickname:', error);
            throw error;
        }
    }, [teamId]);

    return {
        nicknames,
        setNickname,
    };
}
